'use client'

/**
 * PodcastPageClient — Render-Layout + Audio-State der /podcast Seite.
 *
 * - Award-Hero "FGL PODCAST" mit Anton-Mega-Font + Gold-Gradient + Logo
 * - GliderBackdrop/LivingThermal Atmosphaere wie /wertung
 * - Sort-Pillen "Neueste / Aelteste" (.wertung-tab-pill)
 * - Episode-Cards mit Cover, Episode-Nummer-Pille, Titel, Datum,
 *   Beschreibung, Play-Pille
 * - Sticky-Bottom-Player wenn Episode aktiv (Play/Pause + Skip + Progress)
 * - Quick-Badges Spotify/Apple mit direkten Show-Links (2026-06-23)
 *
 * Copyright (c) 2026 Stephan Eich, Berlin, Germany
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import Link from 'next/link'
import { FglLogoSvg } from '@/components/brand/fgl-logo-svg'
import { GliderBackdrop, LivingThermal, Reveal } from '@/components/award'
import type { PodcastFeed, PodcastEpisode } from '@/app/api/public/podcast/route'

interface Props {
  feed: PodcastFeed | null
  error: string | null
}

type Sort = 'newest' | 'oldest'

export function PodcastPageClient({ feed, error }: Props) {
  const [sort, setSort] = useState<Sort>('newest')
  const [currentId, setCurrentId] = useState<string | null>(null)
  const [isPlaying, setIsPlaying] = useState(false)
  const [progress, setProgress] = useState(0) // 0..1
  const [position, setPosition] = useState(0) // seconds
  const [duration, setDuration] = useState(0)
  const [buffering, setBuffering] = useState(false)
  // 2026-06-22: WOW-Detail-Modal — Click auf Episode-Card oeffnet ihn
  const [detailId, setDetailId] = useState<string | null>(null)
  // Playback-Rate-Toggle (Vorspul-Modus): 1 / 1.25 / 1.5 / 2
  const [playbackRate, setPlaybackRate] = useState(1)
  const audioRef = useRef<HTMLAudioElement | null>(null)

  const sorted = useMemo<PodcastEpisode[]>(() => {
    if (!feed?.episodes) return []
    const list = [...feed.episodes]
    list.sort((a, b) => {
      const ta = new Date(a.publishedAt).getTime()
      const tb = new Date(b.publishedAt).getTime()
      return sort === 'newest' ? tb - ta : ta - tb
    })
    return list
  }, [feed?.episodes, sort])

  const current = currentId ? sorted.find((e) => e.id === currentId) ?? null : null

  // Audio-Element zentral verwalten
  useEffect(() => {
    if (!audioRef.current) audioRef.current = new Audio()
    const a = audioRef.current
    const onTime = () => {
      setPosition(a.currentTime)
      if (a.duration > 0) setProgress(a.currentTime / a.duration)
    }
    const onLoaded = () => {
      setDuration(a.duration)
      setBuffering(false)
    }
    const onWaiting = () => setBuffering(true)
    const onCanPlay = () => setBuffering(false)
    const onEnded = () => setIsPlaying(false)
    a.addEventListener('timeupdate', onTime)
    a.addEventListener('loadedmetadata', onLoaded)
    a.addEventListener('waiting', onWaiting)
    a.addEventListener('canplay', onCanPlay)
    a.addEventListener('ended', onEnded)
    return () => {
      a.removeEventListener('timeupdate', onTime)
      a.removeEventListener('loadedmetadata', onLoaded)
      a.removeEventListener('waiting', onWaiting)
      a.removeEventListener('canplay', onCanPlay)
      a.removeEventListener('ended', onEnded)
    }
  }, [])

  const playEpisode = useCallback((ep: PodcastEpisode) => {
    if (!audioRef.current) audioRef.current = new Audio()
    const a = audioRef.current
    if (currentId !== ep.id) {
      a.src = ep.audioUrl
      a.load()
      setCurrentId(ep.id)
      setPosition(0)
      setDuration(ep.durationSeconds ?? 0)
    }
    a.play().then(() => setIsPlaying(true)).catch(() => setIsPlaying(false))
  }, [currentId])

  const pause = useCallback(() => {
    audioRef.current?.pause()
    setIsPlaying(false)
  }, [])

  const togglePlay = useCallback((ep: PodcastEpisode) => {
    if (currentId === ep.id && isPlaying) {
      pause()
    } else {
      playEpisode(ep)
    }
  }, [currentId, isPlaying, pause, playEpisode])

  const skip = useCallback((deltaSec: number) => {
    const a = audioRef.current
    if (!a) return
    const next = Math.max(0, Math.min(a.duration || 0, a.currentTime + deltaSec))
    a.currentTime = next
  }, [])

  const jumpToStart = useCallback(() => {
    const a = audioRef.current
    if (!a) return
    a.currentTime = 0
  }, [])

  const jumpToEnd = useCallback(() => {
    const a = audioRef.current
    if (!a || !a.duration) return
    a.currentTime = Math.max(0, a.duration - 1)
  }, [])

  const seekTo = useCallback((ratio: number) => {
    const a = audioRef.current
    if (!a || !a.duration) return
    a.currentTime = a.duration * Math.max(0, Math.min(1, ratio))
  }, [])

  // Jog-Shuttle: kontinuierliches Scrubben mit beliebiger Sek-pro-Sek-Rate.
  // Positive Rate = Vorlauf, negative = Rücklauf. 0 = stoppt das Scrubben.
  const scrubRateRef = useRef(0)
  const scrubRafRef = useRef<number | null>(null)
  const setScrubRate = useCallback((rateSecPerSec: number) => {
    scrubRateRef.current = rateSecPerSec
    if (rateSecPerSec === 0 && scrubRafRef.current !== null) {
      cancelAnimationFrame(scrubRafRef.current)
      scrubRafRef.current = null
      return
    }
    if (rateSecPerSec !== 0 && scrubRafRef.current === null) {
      let last = performance.now()
      const tick = (now: number) => {
        const dt = (now - last) / 1000
        last = now
        const a = audioRef.current
        if (a && a.duration && scrubRateRef.current !== 0) {
          const next = Math.max(0, Math.min(a.duration, a.currentTime + scrubRateRef.current * dt))
          a.currentTime = next
        }
        if (scrubRateRef.current !== 0) {
          scrubRafRef.current = requestAnimationFrame(tick)
        } else {
          scrubRafRef.current = null
        }
      }
      scrubRafRef.current = requestAnimationFrame(tick)
    }
  }, [])

  // Speed-Toggle 1 → 1.25 → 1.5 → 2 → 1 …
  const cycleSpeed = useCallback(() => {
    const cycle = [1, 1.25, 1.5, 2]
    setPlaybackRate((cur) => {
      const idx = cycle.indexOf(cur)
      const next = cycle[(idx + 1) % cycle.length]
      if (audioRef.current) audioRef.current.playbackRate = next
      return next
    })
  }, [])

  // Bei jedem playEpisode den aktuellen Speed wieder anwenden (Audio neu geladen)
  useEffect(() => {
    if (audioRef.current) audioRef.current.playbackRate = playbackRate
  }, [playbackRate, currentId])

  const closePlayer = useCallback(() => {
    audioRef.current?.pause()
    setCurrentId(null)
    setIsPlaying(false)
    setPosition(0)
    setProgress(0)
    setDuration(0)
  }, [])

  return (
    <div
      data-award="true"
      style={{
        position: 'relative',
        minHeight: '100vh',
        background: 'var(--award-ink, #0a0a0a)',
        color: 'var(--text, #fafafa)',
        overflowX: 'hidden',
      }}
    >
      <GliderBackdrop />
      <LivingThermal />

      {/* Hero */}
      <section
        style={{
          position: 'relative',
          padding: 'clamp(48px, 8vw, 96px) 20px 32px',
          textAlign: 'center',
          zIndex: 1,
        }}
      >
        <Reveal>
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 18,
              flexWrap: 'wrap',
              marginBottom: 18,
            }}
          >
            <div
              className="fgl-logo-frame"
              style={{
                width: 88, height: 88,
                fontSize: 88,
                boxShadow: '0 0 32px color-mix(in oklch, var(--accent) 28%, transparent)',
              }}
            >
              <FglLogoSvg />
            </div>
            <div style={{ textAlign: 'left' }}>
              <div
                className="award-eyebrow"
                style={{ color: 'var(--accent)', fontSize: 13, letterSpacing: '0.18em' }}
              >
                Offizieller Podcast
              </div>
              <div style={{ fontSize: 13, color: 'var(--muted)', marginTop: 2 }}>
                {feed?.show?.author ? `von ${feed.show.author}` : 'von Daniel Wönicker'}
              </div>
            </div>
          </div>
        </Reveal>

        <Reveal>
          <h1
            className="tracking-tight leading-none"
            style={{
              fontFamily: 'var(--font-mega)',
              fontWeight: 400,
              fontSize: 'clamp(46px, 8vw, 110px)',
              letterSpacing: '0.005em',
              textTransform: 'uppercase',
              marginBottom: 14,
            }}
          >
            FGL <span className="award-grad-text">PODCAST</span>
          </h1>
        </Reveal>

        <Reveal>
          <p
            style={{
              maxWidth: 720,
              margin: '0 auto',
              fontSize: 'clamp(14px, 1.6vw, 17px)',
              lineHeight: 1.65,
              color: 'var(--muted)',
              padding: '0 12px',
            }}
          >
            {feed?.show?.description?.slice(0, 320)
              || 'Stimmen, Geschichten, Hintergründe aus der Welt des leistungsorientierten RC-Modellsegelflugs. Alle Folgen kostenfrei anhören — produziert und veröffentlicht von Daniel Wönicker auf mo-pilot.com, hier zum Direkt-Anhören eingebunden.'}
          </p>
        </Reveal>

        {/* Quick-Badges + externer Link */}
        <Reveal>
          <div
            style={{
              marginTop: 26,
              display: 'flex',
              justifyContent: 'center',
              gap: 10,
              flexWrap: 'wrap',
            }}
          >
            <a
              href={feed?.show?.websiteUrl ?? 'https://mo-pilot.com/'}
              target="_blank"
              rel="noopener noreferrer"
              className="wertung-tab-pill"
              data-state="inactive"
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 8,
                padding: '8px 16px', borderRadius: 9999,
                fontWeight: 700, fontSize: 12,
                textTransform: 'uppercase', letterSpacing: '0.06em',
                textDecoration: 'none',
              }}
            >
              🌐 mo-pilot.com
            </a>
            <a
              href="https://open.spotify.com/show/2AkbUgMxnjBdgLpRDkLSBI"
              target="_blank"
              rel="noopener noreferrer"
              className="wertung-tab-pill"
              data-state="inactive"
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 8,
                padding: '8px 16px', borderRadius: 9999,
                fontWeight: 700, fontSize: 12,
                textTransform: 'uppercase', letterSpacing: '0.06em',
                textDecoration: 'none',
              }}
            >
              🟢 Spotify
            </a>
            <a
              href="https://podcasts.apple.com/us/podcast/free-glide-league-podcast/id1813924585"
              target="_blank"
              rel="noopener noreferrer"
              className="wertung-tab-pill"
              data-state="inactive"
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 8,
                padding: '8px 16px', borderRadius: 9999,
                fontWeight: 700, fontSize: 12,
                textTransform: 'uppercase', letterSpacing: '0.06em',
                textDecoration: 'none',
              }}
            >
              🟣 Apple Podcasts
            </a>
          </div>
        </Reveal>
      </section>

      {/* Sort-Pillen */}
      {!error && sorted.length > 0 && (
        <section
          style={{
            position: 'relative',
            zIndex: 1,
            padding: '12px 20px 0',
            display: 'flex',
            justifyContent: 'center',
            gap: 10,
            flexWrap: 'wrap',
          }}
        >
          <button
            type="button"
            onClick={() => setSort('newest')}
            className="wertung-tab-pill wertung-tab-pill-lg"
            data-state={sort === 'newest' ? 'active' : 'inactive'}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 8,
              padding: '10px 20px', borderRadius: 9999,
              fontWeight: 900, fontSize: 12,
              textTransform: 'uppercase', letterSpacing: '0.08em',
              cursor: 'pointer',
            }}
          >
            🆕 Neueste zuerst
          </button>
          <button
            type="button"
            onClick={() => setSort('oldest')}
            className="wertung-tab-pill wertung-tab-pill-lg"
            data-state={sort === 'oldest' ? 'active' : 'inactive'}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 8,
              padding: '10px 20px', borderRadius: 9999,
              fontWeight: 900, fontSize: 12,
              textTransform: 'uppercase', letterSpacing: '0.08em',
              cursor: 'pointer',
            }}
          >
            ⏪ Älteste zuerst
          </button>
          <span
            style={{
              display: 'inline-flex', alignItems: 'center',
              padding: '10px 14px',
              fontSize: 11, fontWeight: 700,
              color: 'var(--muted)',
              letterSpacing: '0.06em', textTransform: 'uppercase',
            }}
          >
            {sorted.length} {sorted.length === 1 ? 'Folge' : 'Folgen'}
          </span>
        </section>
      )}

      {/* Episodes */}
      <section
        style={{
          position: 'relative',
          zIndex: 1,
          maxWidth: 1180,
          margin: '0 auto',
          padding: '32px 16px 200px',
        }}
      >
        {error && (
          <div
            style={{
              padding: 20, borderRadius: 16,
              background: 'color-mix(in oklch, var(--state-danger, #ef4444) 14%, transparent)',
              border: '1px solid color-mix(in oklch, var(--state-danger, #ef4444) 45%, transparent)',
              color: 'var(--text)',
              maxWidth: 640, margin: '40px auto',
            }}
          >
            <div style={{ fontWeight: 900, marginBottom: 4 }}>Podcast-Feed gerade nicht erreichbar</div>
            <div style={{ fontSize: 13, color: 'var(--muted)' }}>
              Wir holen den Feed automatisch alle 6 Stunden frisch. Bitte kurz später nochmal vorbeischauen.
            </div>
          </div>
        )}

        {!error && sorted.length === 0 && (
          <div
            style={{
              padding: 32, borderRadius: 16,
              background: 'var(--card)',
              border: '1px solid var(--border)',
              textAlign: 'center', maxWidth: 600, margin: '40px auto',
              color: 'var(--muted)',
            }}
          >
            Noch keine Episoden zum Anzeigen. Sobald Daniel veröffentlicht, sind sie hier.
          </div>
        )}

        <div
          style={{
            display: 'grid',
            gap: 18,
            gridTemplateColumns: 'repeat(auto-fill, minmax(min(360px, 100%), 1fr))',
          }}
        >
          {sorted.map((ep) => (
            <Reveal key={ep.id}>
              <EpisodeCard
                episode={ep}
                isCurrent={currentId === ep.id}
                isPlaying={currentId === ep.id && isPlaying}
                buffering={currentId === ep.id && buffering}
                onTogglePlay={() => togglePlay(ep)}
                onOpenDetail={() => setDetailId(ep.id)}
              />
            </Reveal>
          ))}
        </div>
      </section>

      {/* WOW-Detail-Modal mit Excerpt + Volltext + Glas-Play-Pille */}
      {detailId && (() => {
        const ep = sorted.find((e) => e.id === detailId)
        if (!ep) return null
        const epActive = currentId === ep.id
        const closeAndPause = () => {
          setScrubRate(0)
          pause()
          setDetailId(null)
        }
        // 2026-06-22 (Stephan-Bug): Prev/Next chronologisch via publishedAt
        // berechnen — Folge-Nummern aus Titel/Audio-URL sind nicht
        // zuverlaessig (manche Folgen haben keine Nummer im Titel, Podlove-
        // Slugs koennen Luecken haben). publishedAt ist immer da.
        const chrono = [...sorted].sort(
          (a, b) => new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime()
        )
        const idx = chrono.findIndex((e) => e.id === ep.id)
        // "Vorherige Folge" = chronologisch aeltere (ein Index spaeter im
        // newest-first Array). "Naechste Folge" = chronologisch juengere.
        const prev = idx >= 0 && idx < chrono.length - 1 ? chrono[idx + 1] : null
        const next = idx > 0 ? chrono[idx - 1] : null
        const jumpToEpisode = (target: PodcastEpisode) => {
          setScrubRate(0)
          // Audio nicht autoplay-en — User entscheidet ueber Play-Pille
          if (currentId === ep.id) pause()
          setDetailId(target.id)
        }
        return (
          <EpisodeDetailModal
            episode={ep}
            previous={prev}
            next={next}
            isCurrent={epActive}
            isPlaying={epActive && isPlaying}
            buffering={epActive && buffering}
            position={epActive ? position : 0}
            duration={epActive ? (duration || ep.durationSeconds || 0) : (ep.durationSeconds || 0)}
            progress={epActive ? progress : 0}
            playbackRate={playbackRate}
            onClose={closeAndPause}
            onTogglePlay={() => togglePlay(ep)}
            onSkipBack15={() => skip(-15)}
            onSkipForward30={() => skip(30)}
            onRewind60={() => skip(-60)}
            onForward60={() => skip(60)}
            onJumpToStart={jumpToStart}
            onJumpToEnd={jumpToEnd}
            onCycleSpeed={cycleSpeed}
            onSeek={seekTo}
            onScrubRate={setScrubRate}
            onJumpToEpisode={jumpToEpisode}
          />
        )
      })()}

      {/* Sticky-Bottom-Player */}
      {current && (
        <StickyPlayer
          episode={current}
          isPlaying={isPlaying}
          buffering={buffering}
          position={position}
          duration={duration || current.durationSeconds || 0}
          progress={progress}
          onTogglePlay={() => togglePlay(current)}
          onSkipBack={() => skip(-15)}
          onSkipForward={() => skip(30)}
          onSeek={seekTo}
          onClose={closePlayer}
        />
      )}
    </div>
  )
}

// ─────────────────────────────────────────────────────────────

function EpisodeCard({
  episode,
  isCurrent,
  isPlaying,
  buffering,
  onTogglePlay,
  onOpenDetail,
}: {
  episode: PodcastEpisode
  isCurrent: boolean
  isPlaying: boolean
  buffering: boolean
  onTogglePlay: () => void
  onOpenDetail: () => void
}) {
  const dateLabel = new Date(episode.publishedAt).toLocaleDateString('de-DE', {
    day: '2-digit', month: 'short', year: 'numeric',
  })
  return (
    <article
      onClick={onOpenDetail}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onOpenDetail()
        }
      }}
      style={{
        background: 'var(--card)',
        border: isCurrent
          ? '1px solid color-mix(in oklch, var(--accent) 60%, transparent)'
          : '1px solid var(--border)',
        borderRadius: 20,
        overflow: 'hidden',
        boxShadow: isCurrent
          ? '0 12px 36px -12px color-mix(in oklch, var(--accent) 50%, transparent)'
          : '0 2px 12px -4px rgba(0,0,0,0.35)',
        transition: 'box-shadow 220ms, border-color 220ms, transform 220ms',
        display: 'flex', flexDirection: 'column',
        cursor: 'pointer',
      }}
      onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.transform = 'translateY(-2px)' }}
      onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.transform = 'translateY(0)' }}
    >
      {/* 2026-06-22 Stephan-Spec: Cover = FGL-Logo statt mo-pilot.com Bilder
          (CSP + Cross-Origin loese sich von selbst, einheitliches Branding). */}
      <div style={{
        position: 'relative', aspectRatio: '1 / 1',
        background:
          'radial-gradient(ellipse at top left, color-mix(in oklch, var(--accent) 30%, transparent) 0%, color-mix(in oklch, var(--accent) 8%, transparent) 45%, var(--award-ink, #0a0a0a) 100%)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        overflow: 'hidden',
      }}>
        <div
          className="fgl-logo-frame"
          style={{
            width: '52%', height: '52%',
            fontSize: 200,
            boxShadow: '0 0 48px color-mix(in oklch, var(--accent) 32%, transparent)',
            opacity: 0.95,
          }}
          aria-label="FGL Podcast"
        >
          <FglLogoSvg />
        </div>
        {episode.episodeNumber !== null && (
          <div
            className="wertung-tab-pill"
            data-state="active"
            style={{
              position: 'absolute', top: 12, left: 12,
              padding: '6px 14px', borderRadius: 9999,
              fontWeight: 900, fontSize: 11,
              textTransform: 'uppercase', letterSpacing: '0.08em',
              display: 'inline-flex', alignItems: 'center', gap: 6,
            }}
          >
            Folge {episode.episodeNumber}
          </div>
        )}
        {episode.durationLabel && (
          <div
            className="wertung-tab-pill"
            data-state="inactive"
            style={{
              position: 'absolute', top: 12, right: 12,
              padding: '6px 12px', borderRadius: 9999,
              fontWeight: 700, fontSize: 11,
            }}
          >
            ⏱ {episode.durationLabel}
          </div>
        )}
      </div>

      <div style={{ padding: 18, display: 'flex', flexDirection: 'column', gap: 10 }}>
        <div style={{ fontSize: 11, color: 'var(--muted)', fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase' }}>
          {dateLabel}
        </div>
        <h3
          style={{
            fontSize: 17, fontWeight: 900, lineHeight: 1.25,
            color: 'var(--text)',
          }}
        >
          {episode.title}
        </h3>
        {episode.description && (
          <p
            style={{
              fontSize: 13, lineHeight: 1.55,
              color: 'var(--muted)',
              display: '-webkit-box',
              WebkitLineClamp: 4,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {episode.description}
          </p>
        )}

        <div style={{ display: 'flex', gap: 8, marginTop: 6, flexWrap: 'wrap', alignItems: 'center' }}>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onTogglePlay() }}
            className="wertung-tab-pill wertung-tab-pill-lg"
            data-state="active"
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 8,
              padding: '10px 22px', borderRadius: 9999,
              fontWeight: 900, fontSize: 13,
              textTransform: 'uppercase', letterSpacing: '0.06em',
              cursor: 'pointer',
            }}
          >
            {buffering ? '⏳ Lädt…' : isPlaying ? '⏸ Pause' : '▶ Abspielen'}
          </button>
          <span
            className="wertung-tab-pill"
            data-state="inactive"
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              padding: '8px 14px', borderRadius: 9999,
              fontWeight: 700, fontSize: 11,
              textTransform: 'uppercase', letterSpacing: '0.05em',
              pointerEvents: 'none',
            }}
          >
            📖 Mehr anzeigen
          </span>
        </div>
      </div>
    </article>
  )
}

// ─────────────────────────────────────────────────────────────

function StickyPlayer({
  episode,
  isPlaying,
  buffering,
  position,
  duration,
  progress,
  onTogglePlay,
  onSkipBack,
  onSkipForward,
  onSeek,
  onClose,
}: {
  episode: PodcastEpisode
  isPlaying: boolean
  buffering: boolean
  position: number
  duration: number
  progress: number
  onTogglePlay: () => void
  onSkipBack: () => void
  onSkipForward: () => void
  onSeek: (ratio: number) => void
  onClose: () => void
}) {
  const seekRef = useRef<HTMLDivElement>(null)

  const handleSeekClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!seekRef.current) return
    const rect = seekRef.current.getBoundingClientRect()
    const ratio = (e.clientX - rect.left) / rect.width
    onSeek(ratio)
  }

  return (
    <div
      style={{
        position: 'fixed',
        left: 0, right: 0, bottom: 0,
        zIndex: 50,
        background: 'color-mix(in oklch, var(--award-ink, #0a0a0a) 88%, transparent)',
        borderTop: '1px solid color-mix(in oklch, var(--accent) 35%, var(--border))',
        backdropFilter: 'blur(14px)',
        boxShadow: '0 -10px 32px -8px rgba(0,0,0,0.55)',
      }}
    >
      <div
        style={{
          maxWidth: 1180, margin: '0 auto',
          padding: '12px 16px',
          display: 'grid',
          gridTemplateColumns: 'auto minmax(0, 1fr) auto',
          gap: 14, alignItems: 'center',
        }}
      >
        {/* Cover + Titel */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
          <div
            style={{
              width: 52, height: 52, borderRadius: 10, overflow: 'hidden',
              background:
                'radial-gradient(ellipse at top left, color-mix(in oklch, var(--accent) 30%, transparent) 0%, var(--award-ink, #0a0a0a) 100%)',
              flexShrink: 0,
              position: 'relative',
              border: '1px solid color-mix(in oklch, var(--accent) 40%, transparent)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            <div
              className="fgl-logo-frame"
              style={{ width: 32, height: 32, fontSize: 32 }}
              aria-hidden
            >
              <FglLogoSvg />
            </div>
          </div>
          <div style={{ minWidth: 0 }}>
            <div
              style={{
                fontSize: 13, fontWeight: 900,
                color: 'var(--text)',
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}
            >
              {episode.title}
            </div>
            <div
              style={{
                fontSize: 11, color: 'var(--muted)', marginTop: 2,
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}
            >
              {episode.episodeNumber !== null ? `Folge ${episode.episodeNumber} · ` : ''}
              {episode.author ?? 'FGL Podcast'}
            </div>
          </div>
        </div>

        {/* Controls + Progress */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 }}>
          <div style={{ display: 'flex', justifyContent: 'center', gap: 8, flexWrap: 'wrap' }}>
            <button
              type="button"
              onClick={onSkipBack}
              className="wertung-tab-pill"
              data-state="inactive"
              style={{
                display: 'inline-flex', alignItems: 'center',
                padding: '6px 12px', borderRadius: 9999,
                fontWeight: 800, fontSize: 11,
                cursor: 'pointer',
              }}
              aria-label="15 Sekunden zurück"
            >
              ⏪ 15
            </button>
            <button
              type="button"
              onClick={onTogglePlay}
              className="wertung-tab-pill wertung-tab-pill-lg"
              data-state="active"
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                padding: '8px 18px', borderRadius: 9999,
                fontWeight: 900, fontSize: 12,
                textTransform: 'uppercase', letterSpacing: '0.05em',
                cursor: 'pointer',
              }}
            >
              {buffering ? '⏳' : isPlaying ? '⏸ Pause' : '▶ Play'}
            </button>
            <button
              type="button"
              onClick={onSkipForward}
              className="wertung-tab-pill"
              data-state="inactive"
              style={{
                display: 'inline-flex', alignItems: 'center',
                padding: '6px 12px', borderRadius: 9999,
                fontWeight: 800, fontSize: 11,
                cursor: 'pointer',
              }}
              aria-label="30 Sekunden vor"
            >
              30 ⏩
            </button>
          </div>
          <div
            ref={seekRef}
            onClick={handleSeekClick}
            style={{
              position: 'relative',
              height: 8, borderRadius: 4,
              background: 'color-mix(in oklch, var(--accent) 14%, transparent)',
              cursor: 'pointer', overflow: 'hidden',
            }}
            aria-label="Fortschritt"
            role="slider"
            aria-valuenow={Math.round(progress * 100)}
            aria-valuemin={0}
            aria-valuemax={100}
          >
            <div
              style={{
                position: 'absolute', inset: 0,
                width: `${Math.max(0, Math.min(1, progress)) * 100}%`,
                background: 'linear-gradient(90deg, var(--accent), color-mix(in oklch, var(--accent) 70%, white))',
                borderRadius: 4,
              }}
            />
          </div>
          <div
            style={{
              display: 'flex', justifyContent: 'space-between',
              fontSize: 10, color: 'var(--muted)',
              fontFamily: 'monospace', letterSpacing: '0.04em',
            }}
          >
            <span>{formatTime(position)}</span>
            <span>{duration > 0 ? formatTime(duration) : '–:––'}</span>
          </div>
        </div>

        <button
          type="button"
          onClick={onClose}
          className="wertung-tab-pill"
          data-state="inactive"
          style={{
            display: 'inline-flex', alignItems: 'center',
            padding: '6px 12px', borderRadius: 9999,
            fontWeight: 800, fontSize: 11,
            cursor: 'pointer',
          }}
          aria-label="Player schließen"
        >
          ✕
        </button>
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────

function EpisodeDetailModal({
  episode,
  previous,
  next,
  isCurrent,
  isPlaying,
  buffering,
  position,
  duration,
  progress,
  playbackRate,
  onClose,
  onTogglePlay,
  onSkipBack15,
  onSkipForward30,
  onRewind60,
  onForward60,
  onJumpToStart,
  onJumpToEnd,
  onCycleSpeed,
  onSeek,
  onScrubRate,
  onJumpToEpisode,
}: {
  episode: PodcastEpisode
  previous: PodcastEpisode | null
  next: PodcastEpisode | null
  isCurrent: boolean
  isPlaying: boolean
  buffering: boolean
  position: number
  duration: number
  progress: number
  playbackRate: number
  onClose: () => void
  onTogglePlay: () => void
  onSkipBack15: () => void
  onSkipForward30: () => void
  onRewind60: () => void
  onForward60: () => void
  onJumpToStart: () => void
  onJumpToEnd: () => void
  onCycleSpeed: () => void
  onSeek: (ratio: number) => void
  onScrubRate: (rateSecPerSec: number) => void
  onJumpToEpisode: (target: PodcastEpisode) => void
}) {
  const seekRef = useRef<HTMLDivElement>(null)
  const [shareNotice, setShareNotice] = useState<string | null>(null)

  // Esc + Body-Scroll-Lock
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      window.removeEventListener('keydown', onKey)
      document.body.style.overflow = prev
    }
  }, [onClose])

  const dateLabel = new Date(episode.publishedAt).toLocaleDateString('de-DE', {
    day: '2-digit', month: 'long', year: 'numeric',
  })

  // Excerpt = erste 2 Absaetze / max 280 Zeichen
  const desc = episode.description ?? ''
  const excerptRaw = desc.split('\n\n')[0] ?? desc
  const excerpt =
    excerptRaw.length > 280 ? excerptRaw.slice(0, 280).trim() + '…' : excerptRaw

  const handleSeek = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!seekRef.current) return
    const rect = seekRef.current.getBoundingClientRect()
    onSeek((e.clientX - rect.left) / rect.width)
  }

  const shareUrl =
    episode.pageUrl
    || (typeof window !== 'undefined'
        ? `${window.location.origin}/podcast`
        : 'https://free-glide-league.com/podcast')

  const handleShare = async () => {
    const title = `FGL Podcast — ${episode.title}`
    const text = excerpt || episode.title
    try {
      if (typeof navigator !== 'undefined' && (navigator as any).share) {
        await (navigator as any).share({ title, text, url: shareUrl })
        setShareNotice('Geteilt ✓')
      } else if (typeof navigator !== 'undefined' && navigator.clipboard) {
        await navigator.clipboard.writeText(shareUrl)
        setShareNotice('🔗 Link kopiert')
      } else {
        setShareNotice(shareUrl)
      }
    } catch (_) {
      // User-Cancel oder Fehler — leise
      return
    }
    setTimeout(() => setShareNotice(null), 2500)
  }

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        // 2026-06-22 Stephan-Bug: Top-Menue (FglHeader) soll sichtbar bleiben
        // wenn der Modal offen ist. Backdrop beginnt unter dem Sticky-Header.
        top: 'var(--fgl-header-height, 64px)',
        left: 0, right: 0, bottom: 0,
        zIndex: 9999,
        background: 'color-mix(in oklch, var(--award-ink, #0a0a0a) 86%, transparent)',
        backdropFilter: 'blur(20px)',
        display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
        padding: 16,
        overflowY: 'auto',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          position: 'relative',
          background:
            'linear-gradient(180deg, color-mix(in oklch, var(--card) 90%, transparent) 0%, var(--card) 100%)',
          border: '1px solid color-mix(in oklch, var(--accent) 45%, var(--border))',
          borderRadius: 24,
          maxWidth: 760, width: '100%',
          marginTop: '4vh', marginBottom: '4vh',
          padding: 0,
          boxShadow:
            '0 32px 100px -20px color-mix(in oklch, var(--accent) 55%, transparent),' +
            ' 0 0 80px -20px color-mix(in oklch, var(--accent) 35%, transparent)',
          overflow: 'hidden',
        }}
      >
        {/* Close */}
        <button
          onClick={onClose}
          type="button"
          aria-label="Schließen"
          style={{
            position: 'absolute', top: 16, right: 16, zIndex: 2,
            width: 40, height: 40, borderRadius: '50%',
            background: 'color-mix(in oklch, var(--award-ink, #0a0a0a) 60%, transparent)',
            border: '1px solid color-mix(in oklch, var(--accent) 35%, var(--border))',
            color: 'var(--text)', fontSize: 20,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: 'pointer', backdropFilter: 'blur(8px)',
          }}
        >×</button>

        {/* HERO mit grossem FGL-Logo + Pillen */}
        <div
          style={{
            position: 'relative',
            background:
              'radial-gradient(ellipse at top, color-mix(in oklch, var(--accent) 28%, transparent) 0%, color-mix(in oklch, var(--accent) 6%, transparent) 50%, var(--award-ink, #0a0a0a) 100%)',
            padding: '40px 28px 28px',
            textAlign: 'center',
          }}
        >
          <div
            className="fgl-logo-frame"
            style={{
              width: 130, height: 130,
              fontSize: 130,
              margin: '0 auto 18px',
              boxShadow: '0 0 60px color-mix(in oklch, var(--accent) 38%, transparent)',
            }}
            aria-hidden
          >
            <FglLogoSvg />
          </div>

          <div
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 8,
              flexWrap: 'wrap', justifyContent: 'center',
              marginBottom: 12,
            }}
          >
            {episode.episodeNumber !== null && (
              <span
                className="wertung-tab-pill wertung-tab-pill-lg"
                data-state="active"
                style={{
                  padding: '8px 18px', borderRadius: 9999,
                  fontWeight: 900, fontSize: 12,
                  textTransform: 'uppercase', letterSpacing: '0.08em',
                }}
              >
                Folge {episode.episodeNumber}
              </span>
            )}
            {episode.durationLabel && (
              <span
                className="wertung-tab-pill"
                data-state="inactive"
                style={{
                  padding: '8px 14px', borderRadius: 9999,
                  fontWeight: 700, fontSize: 11,
                  textTransform: 'uppercase', letterSpacing: '0.06em',
                }}
              >
                ⏱ {episode.durationLabel}
              </span>
            )}
            <span
              className="wertung-tab-pill"
              data-state="inactive"
              style={{
                padding: '8px 14px', borderRadius: 9999,
                fontWeight: 700, fontSize: 11,
                textTransform: 'uppercase', letterSpacing: '0.06em',
              }}
            >
              📅 {dateLabel}
            </span>
          </div>

          <h2
            style={{
              fontSize: 'clamp(20px, 3vw, 28px)',
              fontWeight: 900, lineHeight: 1.2,
              color: 'var(--text)',
              maxWidth: 640, margin: '0 auto',
            }}
          >
            {episode.title}
          </h2>

          {/* TONBANDGERAET — Schiebeschalter-Reihe mit Transport-Glas-Pillen */}
          <div
            style={{
              marginTop: 24,
              maxWidth: 620, margin: '24px auto 0',
              padding: 18,
              borderRadius: 20,
              background: 'color-mix(in oklch, var(--award-ink, #0a0a0a) 60%, transparent)',
              border: '1px solid color-mix(in oklch, var(--accent) 40%, var(--border))',
              backdropFilter: 'blur(14px)',
              boxShadow: '0 12px 32px -10px color-mix(in oklch, var(--accent) 40%, transparent)',
            }}
          >
            {/* Transport-Reihe: ⏮ ⏪⏪ ⏪ ▶/⏸ ⏩ ⏩⏩ ⏭ */}
            <div
              style={{
                display: 'flex', justifyContent: 'center', alignItems: 'center',
                gap: 6, flexWrap: 'wrap',
              }}
            >
              <TransportPill onClick={onJumpToStart} disabled={!isCurrent} label="Zum Anfang" aria="Zum Anfang">⏮</TransportPill>
              <TransportPill onClick={onRewind60} disabled={!isCurrent} label="60 s zurück" aria="60 Sekunden zurück">⏪⏪</TransportPill>
              <TransportPill onClick={onSkipBack15} disabled={!isCurrent} label="15 s zurück" aria="15 Sekunden zurück">⏪</TransportPill>
              <button
                type="button"
                onClick={onTogglePlay}
                className="wertung-tab-pill wertung-tab-pill-lg"
                data-state="active"
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 8,
                  padding: '14px 28px', borderRadius: 9999,
                  fontWeight: 900, fontSize: 14,
                  textTransform: 'uppercase', letterSpacing: '0.07em',
                  cursor: 'pointer',
                  margin: '0 4px',
                }}
              >
                {buffering
                  ? '⏳ Lädt…'
                  : isCurrent && isPlaying
                    ? '⏸ Pause'
                    : '▶ Play'}
              </button>
              <TransportPill onClick={onSkipForward30} disabled={!isCurrent} label="30 s vor" aria="30 Sekunden vor">⏩</TransportPill>
              <TransportPill onClick={onForward60} disabled={!isCurrent} label="60 s vor" aria="60 Sekunden vor">⏩⏩</TransportPill>
              <TransportPill onClick={onJumpToEnd} disabled={!isCurrent} label="Zum Ende" aria="Zum Ende">⏭</TransportPill>
            </div>

            {/* JOG-SHUTTLE: maus druecken, links/rechts schieben — exponentieller Vor/Rueck-Lauf */}
            <JogShuttle disabled={!isCurrent} onScrubRate={onScrubRate} />

            {/* Progress-Bar */}
            <div
              ref={seekRef}
              onClick={isCurrent ? handleSeek : undefined}
              style={{
                position: 'relative',
                height: 10, borderRadius: 5,
                marginTop: 18,
                background: 'color-mix(in oklch, var(--accent) 14%, transparent)',
                cursor: isCurrent ? 'pointer' : 'default',
                opacity: isCurrent ? 1 : 0.6,
                overflow: 'hidden',
              }}
              aria-label="Fortschritt"
              role="slider"
              aria-valuenow={Math.round(progress * 100)}
              aria-valuemin={0}
              aria-valuemax={100}
            >
              <div
                style={{
                  position: 'absolute', inset: 0,
                  width: `${Math.max(0, Math.min(1, progress)) * 100}%`,
                  background: 'linear-gradient(90deg, var(--accent), color-mix(in oklch, var(--accent) 60%, white))',
                  borderRadius: 5,
                  transition: 'width 200ms linear',
                }}
              />
            </div>

            {/* Tonband-Counter: Position / Total + Prozent + Restzeit */}
            <div
              style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                marginTop: 6, gap: 8,
                fontFamily: 'monospace',
              }}
            >
              <span style={{ fontSize: 11, color: 'var(--muted)', letterSpacing: '0.04em' }}>
                <span style={{ color: 'var(--text)', fontWeight: 800 }}>{formatTime(position)}</span>
                {' / '}
                {duration > 0 ? formatTime(duration) : '–:––'}
              </span>
              <span
                style={{
                  fontSize: 13, fontWeight: 900,
                  color: 'var(--accent)',
                  letterSpacing: '0.06em',
                  padding: '2px 10px', borderRadius: 9999,
                  background: 'color-mix(in oklch, var(--accent) 14%, transparent)',
                  border: '1px solid color-mix(in oklch, var(--accent) 35%, transparent)',
                }}
              >
                {duration > 0 ? `${Math.round(progress * 100)} %` : '— %'}
              </span>
              <span style={{ fontSize: 11, color: 'var(--muted)', letterSpacing: '0.04em' }}>
                {duration > 0 ? `−${formatTime(Math.max(0, duration - position))}` : '−–:––'}
              </span>
            </div>

            {/* Speed + Share */}
            <div style={{ marginTop: 16, display: 'flex', justifyContent: 'center', gap: 10, flexWrap: 'wrap' }}>
              <button
                type="button"
                onClick={onCycleSpeed}
                className="wertung-tab-pill"
                data-state={playbackRate !== 1 ? 'active' : 'inactive'}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  padding: '8px 16px', borderRadius: 9999,
                  fontWeight: 800, fontSize: 12,
                  textTransform: 'uppercase', letterSpacing: '0.06em',
                  cursor: 'pointer',
                }}
                aria-label="Wiedergabe-Geschwindigkeit umschalten"
              >
                ⚡ {playbackRate}×
              </button>
              <button
                type="button"
                onClick={handleShare}
                className="wertung-tab-pill"
                data-state="inactive"
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  padding: '8px 16px', borderRadius: 9999,
                  fontWeight: 800, fontSize: 12,
                  textTransform: 'uppercase', letterSpacing: '0.06em',
                  cursor: 'pointer',
                }}
              >
                {shareNotice ?? '🔗 Folge teilen'}
              </button>
            </div>
          </div>

          {/* Prev/Next-Navigation — chronologisch */}
          {(previous || next) && (
            <div
              style={{
                marginTop: 18,
                maxWidth: 620, margin: '18px auto 0',
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: 10,
              }}
            >
              <NavCard
                episode={previous}
                direction="prev"
                onClick={() => previous && onJumpToEpisode(previous)}
              />
              <NavCard
                episode={next}
                direction="next"
                onClick={() => next && onJumpToEpisode(next)}
              />
            </div>
          )}
        </div>

        {/* BODY: Excerpt + Volltext */}
        <div style={{ padding: '28px 28px 32px' }}>
          {excerpt && (
            <div
              style={{
                padding: '16px 18px',
                borderRadius: 14,
                background: 'color-mix(in oklch, var(--accent) 8%, transparent)',
                border: '1px solid color-mix(in oklch, var(--accent) 28%, transparent)',
                fontSize: 15, lineHeight: 1.65,
                fontWeight: 600, fontStyle: 'italic',
                color: 'var(--text)',
                marginBottom: 20,
              }}
            >
              <div
                style={{
                  fontSize: 10, fontWeight: 900,
                  color: 'var(--accent)',
                  textTransform: 'uppercase', letterSpacing: '0.12em',
                  marginBottom: 6,
                  fontStyle: 'normal',
                }}
              >
                Worum es geht
              </div>
              {excerpt}
            </div>
          )}

          {desc && desc.length > excerpt.length && (
            <div>
              <div
                style={{
                  fontSize: 10, fontWeight: 900,
                  color: 'var(--muted)',
                  textTransform: 'uppercase', letterSpacing: '0.12em',
                  marginBottom: 10,
                }}
              >
                Show-Notes
              </div>
              <div
                style={{
                  fontSize: 14, lineHeight: 1.75,
                  color: 'var(--text)',
                  whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                }}
              >
                {desc}
              </div>
            </div>
          )}

          {/* Bottom Schliessen */}
          <div
            style={{
              marginTop: 24, paddingTop: 18,
              borderTop: '1px solid var(--border)',
              display: 'flex', gap: 10, flexWrap: 'wrap', justifyContent: 'center',
            }}
          >
            <button
              type="button"
              onClick={onClose}
              className="wertung-tab-pill"
              data-state="inactive"
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                padding: '12px 24px', borderRadius: 9999,
                fontWeight: 800, fontSize: 12,
                textTransform: 'uppercase', letterSpacing: '0.06em',
                cursor: 'pointer',
              }}
            >
              Schließen
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────

// ─────────────────────────────────────────────────────────────

function NavCard({
  episode,
  direction,
  onClick,
}: {
  episode: PodcastEpisode | null
  direction: 'prev' | 'next'
  onClick: () => void
}) {
  const disabled = !episode
  const isPrev = direction === 'prev'
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="wertung-tab-pill"
      data-state="inactive"
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: isPrev ? 'flex-start' : 'flex-end',
        textAlign: isPrev ? 'left' : 'right',
        gap: 4,
        padding: '12px 16px',
        borderRadius: 14,
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.4 : 1,
        width: '100%',
        background:
          'linear-gradient(180deg, color-mix(in oklch, var(--card) 92%, transparent), var(--card))',
      }}
    >
      <span
        style={{
          fontSize: 10, fontWeight: 900,
          color: 'var(--accent)',
          letterSpacing: '0.14em',
          textTransform: 'uppercase',
        }}
      >
        {isPrev ? '⏮ Vorherige Folge' : 'Nächste Folge ⏭'}
      </span>
      <span
        style={{
          fontSize: 13, fontWeight: 800,
          color: 'var(--text)', lineHeight: 1.35,
          overflow: 'hidden', textOverflow: 'ellipsis',
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
          wordBreak: 'break-word',
          width: '100%',
        }}
      >
        {episode
          ? `${episode.episodeNumber !== null ? `Folge ${episode.episodeNumber} — ` : ''}${episode.title}`
          : isPrev
            ? 'Keine vorherige Folge'
            : 'Keine nächste Folge'}
      </span>
    </button>
  )
}

// ─────────────────────────────────────────────────────────────

function TransportPill({
  children,
  onClick,
  disabled,
  label,
  aria,
}: {
  children: React.ReactNode
  onClick: () => void
  disabled: boolean
  label: string
  aria: string
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="wertung-tab-pill"
      data-state="inactive"
      title={label}
      aria-label={aria}
      style={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        minWidth: 38,
        padding: '8px 10px', borderRadius: 9999,
        fontWeight: 800, fontSize: 12,
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.4 : 1,
      }}
    >
      {children}
    </button>
  )
}

// ─────────────────────────────────────────────────────────────

/**
 * JogShuttle — Press-and-Drag Scrubber im Tonbandgeraet-Stil.
 *
 * User klickt den Knob, haelt gedrueckt, bewegt nach rechts (Vorlauf) oder
 * links (Rueckspul). Je weiter er bewegt, desto schneller spult er —
 * exponentielle Mapping vom Pixel-Offset auf Sekunden-pro-Sekunde:
 *   offsetPx in [0..maxPx] → scrubSpeed in [0..maxSpeed] ueber Pow(x,2.2)
 *   Vorzeichen vom Offset = Spul-Richtung.
 * Bei Pointer-Release fliegt der Knob zurueck zur Mitte und das Spulen
 * stoppt.
 */
function JogShuttle({
  disabled,
  onScrubRate,
}: {
  disabled: boolean
  onScrubRate: (rateSecPerSec: number) => void
}) {
  const railRef = useRef<HTMLDivElement>(null)
  const knobRef = useRef<HTMLDivElement>(null)
  const [dragging, setDragging] = useState(false)
  const [offsetPx, setOffsetPx] = useState(0)
  const maxOffsetRef = useRef(160) // halbe Rail-Breite, wird beim Drag-Start gemessen
  const lastClientXRef = useRef(0)
  const startXRef = useRef(0)

  // Exponentielle Mapping-Funktion
  const mapToRate = (offset: number, maxOffset: number) => {
    const sign = Math.sign(offset)
    const norm = Math.min(1, Math.abs(offset) / Math.max(1, maxOffset))
    const exp = Math.pow(norm, 2.2)
    const MAX_SPEED = 120 // Sek/Sek bei Vollausschlag (sehr schnell)
    return sign * exp * MAX_SPEED
  }

  useEffect(() => {
    if (!dragging) return
    const handleMove = (clientX: number) => {
      lastClientXRef.current = clientX
      const delta = clientX - startXRef.current
      const clamped = Math.max(-maxOffsetRef.current, Math.min(maxOffsetRef.current, delta))
      setOffsetPx(clamped)
      onScrubRate(mapToRate(clamped, maxOffsetRef.current))
    }
    const onMouseMove = (e: MouseEvent) => handleMove(e.clientX)
    const onTouchMove = (e: TouchEvent) => {
      if (e.touches[0]) handleMove(e.touches[0].clientX)
    }
    const stop = () => {
      setDragging(false)
      setOffsetPx(0)
      onScrubRate(0)
    }
    window.addEventListener('mousemove', onMouseMove)
    window.addEventListener('touchmove', onTouchMove)
    window.addEventListener('mouseup', stop)
    window.addEventListener('touchend', stop)
    window.addEventListener('touchcancel', stop)
    return () => {
      window.removeEventListener('mousemove', onMouseMove)
      window.removeEventListener('touchmove', onTouchMove)
      window.removeEventListener('mouseup', stop)
      window.removeEventListener('touchend', stop)
      window.removeEventListener('touchcancel', stop)
    }
  }, [dragging, onScrubRate])

  const startDrag = (clientX: number) => {
    if (disabled) return
    if (railRef.current) {
      maxOffsetRef.current = Math.max(80, railRef.current.getBoundingClientRect().width / 2 - 32)
    }
    startXRef.current = clientX
    lastClientXRef.current = clientX
    setOffsetPx(0)
    setDragging(true)
  }

  // Indikator-Label (zeigt aktuelle "Spul-Stufe" beim Drag)
  const ratio = offsetPx / maxOffsetRef.current
  const indicator =
    !dragging
      ? '⇆ ZIEHEN ZUM SPULEN'
      : Math.abs(ratio) < 0.02
        ? '—— MITTE ——'
        : ratio < 0
          ? `⏪ RÜCK ${Math.round(Math.abs(mapToRate(offsetPx, maxOffsetRef.current)))}×`
          : `⏩ VOR ${Math.round(mapToRate(offsetPx, maxOffsetRef.current))}×`

  return (
    <div
      ref={railRef}
      style={{
        position: 'relative',
        marginTop: 14,
        height: 56,
        borderRadius: 16,
        background:
          'linear-gradient(180deg, color-mix(in oklch, var(--award-ink, #0a0a0a) 70%, transparent), color-mix(in oklch, var(--accent) 8%, transparent))',
        border: '1px solid color-mix(in oklch, var(--accent) 30%, var(--border))',
        boxShadow:
          'inset 0 2px 8px color-mix(in oklch, var(--award-ink, #0a0a0a) 60%, transparent),' +
          ' inset 0 0 0 1px color-mix(in oklch, var(--accent) 15%, transparent)',
        overflow: 'hidden',
        touchAction: 'none',
        opacity: disabled ? 0.45 : 1,
        cursor: disabled ? 'not-allowed' : 'ew-resize',
        userSelect: 'none',
      }}
    >
      {/* Mittel-Markierung */}
      <div
        style={{
          position: 'absolute', top: 8, bottom: 8,
          left: '50%', width: 2, marginLeft: -1,
          background: 'color-mix(in oklch, var(--accent) 38%, transparent)',
          borderRadius: 1,
        }}
      />
      {/* Skala-Markierungen */}
      {[-3, -2, -1, 1, 2, 3].map((i) => (
        <div
          key={i}
          style={{
            position: 'absolute', top: 22, bottom: 22,
            left: `calc(50% + ${i * 14}%)`, width: 1,
            background: 'color-mix(in oklch, var(--muted) 35%, transparent)',
          }}
        />
      ))}
      {/* Label */}
      <div
        style={{
          position: 'absolute', left: 0, right: 0, top: 6,
          textAlign: 'center',
          fontSize: 10, fontWeight: 900,
          color: 'var(--accent)',
          letterSpacing: '0.14em',
          textTransform: 'uppercase',
          pointerEvents: 'none',
        }}
      >
        {indicator}
      </div>
      {/* Knob */}
      <div
        ref={knobRef}
        onMouseDown={(e) => startDrag(e.clientX)}
        onTouchStart={(e) => {
          if (e.touches[0]) startDrag(e.touches[0].clientX)
        }}
        style={{
          position: 'absolute',
          left: `calc(50% + ${offsetPx}px - 24px)`,
          top: 18, width: 48, height: 28,
          borderRadius: 9999,
          background:
            'linear-gradient(180deg, color-mix(in oklch, var(--accent) 80%, white), color-mix(in oklch, var(--accent) 65%, var(--award-ink)))',
          border: '1px solid color-mix(in oklch, var(--accent) 85%, transparent)',
          boxShadow:
            '0 4px 10px -2px color-mix(in oklch, var(--accent) 55%, transparent),' +
            ' inset 0 1px 0 color-mix(in oklch, white 25%, transparent),' +
            ' inset 0 -2px 0 color-mix(in oklch, var(--award-ink) 35%, transparent)',
          cursor: disabled ? 'not-allowed' : (dragging ? 'grabbing' : 'grab'),
          transition: dragging ? 'none' : 'left 250ms cubic-bezier(0.16, 1, 0.3, 1)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: 'var(--award-ink, #0a0a0a)',
          fontWeight: 900, fontSize: 11,
          letterSpacing: '0.06em',
        }}
        aria-label="Jog-Shuttle: zum Spulen ziehen"
        role="slider"
      >
        ◀●▶
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────

function formatTime(sec: number): string {
  if (!Number.isFinite(sec) || sec < 0) return '0:00'
  const total = Math.floor(sec)
  const h = Math.floor(total / 3600)
  const m = Math.floor((total % 3600) / 60)
  const s = total % 60
  if (h > 0) return `${h}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`
  return `${m}:${s.toString().padStart(2, '0')}`
}
